var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useRef, useCallback, useEffect } from 'react';
import { toast as redesignToast } from 'root/src/client/v2/common/components/ui/sonner';
// Drives a Button's loading/success/error states for an async action, with auto-reset timers.
// Pass the action to `run`; optionally pass `toast` to wire the loading/success/error toast.
export const useAsyncAction = () => {
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const timers = useRef([]);
    useEffect(() => () => {
        timers.current.forEach(clearTimeout);
    }, []);
    const run = useCallback((action, options = {}) => __awaiter(void 0, void 0, void 0, function* () {
        const { onSuccess, successHold = 1100, errorHold = 1300, toast: toastMessages } = options;
        setError(false);
        setSuccess(false);
        setLoading(true);
        const promise = action();
        if (toastMessages) {
            void redesignToast.promise(promise, {
                loading: toastMessages.loading,
                success: toastMessages.success,
                error: caught => (typeof toastMessages.error === 'function' ? toastMessages.error(caught) : toastMessages.error),
            });
        }
        try {
            yield promise;
            setLoading(false);
            setSuccess(true);
            timers.current.push(setTimeout(() => {
                setSuccess(false);
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess();
            }, successHold));
            return true;
        }
        catch (caught) {
            setLoading(false);
            setError(true);
            timers.current.push(setTimeout(() => setError(false), errorHold));
            // With a toast, toast.promise already surfaced the error; don't re-throw (would be
            // an unhandled rejection at the call site). Without one, re-throw so the caller can
            // show its own feedback.
            if (toastMessages) {
                return false;
            }
            throw caught;
        }
    }), []);
    return { loading, success, error, run };
};
